// Cloudflare Turnstile helper.
//
// The sitekey is PUBLIC and is meant to ship in the bundle. The matching secret key
// lives only in the Lambda (TURNSTILE_SECRET) and must never appear here.
//
// Get both from the Cloudflare dashboard: Turnstile > Add widget, with
// thecoffeebreak.co (and localhost for dev) listed as allowed hostnames.
export const TURNSTILE_SITEKEY = "0x4AAAAAAE2gVA10A-GidjVb";

const SCRIPT_ID = "cf-turnstile-script";
const READY_CALLBACK = "__tcbTurnstileReady";
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js" +
  `?render=explicit&onload=${READY_CALLBACK}`;

let readyPromise = null;

// Loads the Turnstile script once per page, however many forms ask for it.
export function loadTurnstile() {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    window[READY_CALLBACK] = () => resolve(window.turnstile);

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) return;

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });

  return readyPromise;
}

// Renders a widget into `el` and calls onToken(token) whenever a token becomes
// available, or onToken("") when one expires or errors. Resolves to a widget id.
// Returns null if Turnstile cannot load, so callers can decide to carry on.
export async function renderTurnstile(el, onToken) {
  if (!el) return null;
  try {
    const turnstile = await loadTurnstile();
    return turnstile.render(el, {
      sitekey: TURNSTILE_SITEKEY,
      // Pinned light rather than the default "auto": auto follows the visitor's
      // system preference, so the widget would render dark for some people
      // against a cream page.
      theme: "light",
      // Hidden unless the visitor actually has to solve something. Keeps the
      // layout clean without losing the interactive fallback that full
      // Invisible mode gives up.
      appearance: "interaction-only",
      // Turnstile injects a ~72px placeholder even when nothing is shown, so the
      // container is collapsed by CSS and only opened for a real challenge.
      // These two are Cloudflare's documented hooks for exactly that moment.
      "before-interactive-callback": () => el.classList.add("ts-interactive"),
      "after-interactive-callback": () => el.classList.remove("ts-interactive"),
      callback: (token) => onToken(token),
      "error-callback": () => onToken(""),
      "expired-callback": () => onToken(""),
      "timeout-callback": () => onToken(""),
    });
  } catch (err) {
    // Network blocked, script blocked by an extension, etc. The Lambda decides
    // whether a tokenless request is allowed; the form should not break here.
    console.warn("Turnstile unavailable:", err.message);
    return null;
  }
}

// Tokens are single-use and expire after 300s, so a form that stays open or is
// submitted twice needs a fresh one.
export function resetTurnstile(widgetId) {
  if (window.turnstile && widgetId !== null && widgetId !== undefined) {
    try {
      window.turnstile.reset(widgetId);
    } catch (err) {
      console.warn("Turnstile reset failed:", err.message);
    }
  }
}
